/**
 * File Tree
 *
 * Displays a compact hierarchical file and folder structure.
 *
 * Primary responsibilities:
 * - Render nested file paths as expandable folders
 * - Support controlled and uncontrolled expanded state
 * - Notify callers when a file or folder is selected
 *
 * Dependencies:
 * - components/ui/button
 */

'use client'

import {
  type ComponentProps,
  type ReactNode,
  createContext,
  useContext,
  useMemo,
  useState
} from 'react'
import { ChevronRight, File, Folder, FolderOpen } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

type FileTreeContextValue = {
  expanded: Set<string>
  selectedPath?: string
  togglePath: (path: string) => void
  selectPath: (path: string) => void
}

export type FileTreeProps = ComponentProps<'div'> & {
  expanded?: Set<string>
  defaultExpanded?: Set<string>
  selectedPath?: string
  onSelect?: (path: string) => void
  onExpandedChange?: (expanded: Set<string>) => void
}

export type FileTreeFolderProps = ComponentProps<'div'> & {
  path: string
  name: string
}

export type FileTreeFileProps = ComponentProps<'button'> & {
  path: string
  name: string
  icon?: ReactNode
}

const FileTreeContext = createContext<FileTreeContextValue | null>(null)

// --------------

function useFileTree() {
  const context = useContext(FileTreeContext)
  if (!context) {
    throw new Error('FileTree components must be used inside FileTree')
  }

  return context
}

// --------------

function cloneExpanded(expanded: Set<string>, path: string) {
  const next = new Set(expanded)
  if (next.has(path)) {
    next.delete(path)
  } else {
    next.add(path)
  }

  return next
}

// --------------

export function FileTree({
  expanded,
  defaultExpanded = new Set(),
  selectedPath,
  onSelect,
  onExpandedChange,
  className,
  children,
  ...props
}: FileTreeProps) {
  const [internalExpanded, setInternalExpanded] = useState(defaultExpanded)
  const activeExpanded = expanded ?? internalExpanded

  const contextValue = useMemo<FileTreeContextValue>(() => {
    return {
      expanded: activeExpanded,
      selectedPath,
      togglePath: (path) => {
        const next = cloneExpanded(activeExpanded, path)
        if (!expanded) {
          setInternalExpanded(next)
        }
        onExpandedChange?.(next)
      },
      selectPath: (path) => onSelect?.(path)
    }
  }, [activeExpanded, expanded, onExpandedChange, onSelect, selectedPath])

  return (
    <FileTreeContext.Provider value={contextValue}>
      <div
        className={cn(
          'max-h-72 overflow-auto rounded-md border border-border/70 bg-background/70 p-1 text-xs',
          className
        )}
        role="tree"
        {...props}
      >
        {children}
      </div>
    </FileTreeContext.Provider>
  )
}

// --------------

export function FileTreeIcon({ className, ...props }: ComponentProps<'span'>) {
  return (
    <span
      className={cn('inline-flex size-4 shrink-0 items-center justify-center', className)}
      {...props}
    />
  )
}

export function FileTreeName({ className, ...props }: ComponentProps<'span'>) {
  return <span className={cn('min-w-0 truncate', className)} {...props} />
}

export function FileTreeActions({ className, onClick, ...props }: ComponentProps<'span'>) {
  return (
    <span
      className={cn('ml-auto inline-flex shrink-0 items-center gap-1', className)}
      onClick={(event) => {
        event.stopPropagation()
        onClick?.(event)
      }}
      {...props}
    />
  )
}

// --------------

export function FileTreeFolder({
  path,
  name,
  className,
  children,
  ...props
}: FileTreeFolderProps) {
  const { expanded, selectedPath, togglePath, selectPath } = useFileTree()
  const isExpanded = expanded.has(path)
  const isSelected = selectedPath === path

  return (
    <div role="treeitem" aria-expanded={isExpanded} aria-selected={isSelected} {...props}>
      <Button
        type="button"
        variant="ghost"
        className={cn(
          'h-7 w-full justify-start gap-1.5 rounded px-1.5 text-xs font-normal',
          isSelected && 'bg-muted text-foreground',
          className
        )}
        onClick={() => {
          togglePath(path)
          selectPath(path)
        }}
      >
        <ChevronRight
          className={cn('size-3.5 shrink-0 transition-transform', isExpanded && 'rotate-90')}
        />
        <FileTreeIcon>
          {isExpanded ? <FolderOpen className="size-3.5" /> : <Folder className="size-3.5" />}
        </FileTreeIcon>
        <FileTreeName>{name}</FileTreeName>
      </Button>
      {isExpanded ? <div className="ml-4 border-l border-border/60 pl-1">{children}</div> : null}
    </div>
  )
}

// --------------

export function FileTreeFile({
  path,
  name,
  icon,
  className,
  ...props
}: FileTreeFileProps) {
  const { selectedPath, selectPath } = useFileTree()
  const isSelected = selectedPath === path

  return (
    <button
      type="button"
      role="treeitem"
      aria-selected={isSelected}
      className={cn(
        'flex h-7 w-full items-center gap-1.5 rounded px-1.5 text-left text-xs hover:bg-muted',
        isSelected && 'bg-muted text-foreground',
        className
      )}
      onClick={() => selectPath(path)}
      {...props}
    >
      <span className="size-3.5 shrink-0" />
      <FileTreeIcon>{icon ?? <File className="size-3.5" />}</FileTreeIcon>
      <FileTreeName>{name}</FileTreeName>
    </button>
  )
}
